/**
 * @module interpolator
 * @since 1.9.0
 */

/**
 * Valid types of results for the interpolated string
 * @private
 * @type {Array}
 * @member {String|Number}
 */
const VALID_RESULT_TYPES = Object.seal(['string', 'number']);

/**
 * Generates an interpolator, using an array of RegExp matchers.
 * @param  {...RegExp} replacers RegExp matchers to use when interpolating given strings.
 * @returns {Function} An interpolation function.
 *
 * @example
 *
 * const interpolate = interpolator(/\${([^{}]*)}/gm);
 *
 * interpolate('Hello, ${name}', {name: 'Martin'}); // "Hello, Martin"
 */
export const interpolator = (...replacers) =>

    /**
     * Scan the string for matches to the provided RegExp matchers and replace them with the provided data.
     * @param  {String} string The string to interpolate.
     * @param  {Object} data   The data to replace with.
     * @return {String}
     */
    function interpolate(string = '', data) {
        if (typeof string !== 'string') {
            throw new TypeError(`interpolator expects first argument to be a string, got a ${typeof string}`);
        }

        if (!data) {
            return string;
        }

        /**
         * Replace method build with internal reference to the passed in data structure
         * @param  {String} haystack The full string match
         * @param  {String} needle   The content to identify as data member
         * @return {String}          Found value
         */
        function replace(haystack, needle) {
            const replacement = data[needle.trim()];

            return ~VALID_RESULT_TYPES.indexOf(typeof replacement) ? replacement : haystack;
        }

        return replacers.reduce((string, replacer) => string.replace(replacer, replace), string);
    };
